/**
 * TODO: Automatic Discounts - Main JavaScript Module
 *
 * NWH Sale Prices Module
 * Manages automatic discount price display using metaobject data
 *
 * Usage:
 * 1. Initialize with price data: NWHSalePrices.init(salePricesArray)
 * 2. Get price by SKU: NWHSalePrices.getSalePrice('SKU123')
 * 3. Auto-update elements: Add data-sale-price-sku="SKU123" to any element
 *
 * Related files:
 * - snippets/nwh-sale-prices-init.liquid (initialization)
 * - snippets/nwh-product-view.liquid (Vue watcher)
 * - layout/theme.liquid (include statement)
 */

window.NWHSalePrices = (function() {
  let salePricesData = [];
  let initialized = false;

  /**
   * Initialize the module with sale price data
   * @param {Array} data - Array of {sku: string, price: string} objects
   */
  function init(data) {
    if (!Array.isArray(data)) {
      return;
    }

    salePricesData = data;
    initialized = true;

    // Auto-update any elements with data attributes
    updateAllElements();
  }

  /**
   * Get sale price for a specific SKU
   * @param {string} sku - Product variant SKU
   * @returns {Object|null} - {sku, price, priceValue} or null if not found
   */
  function getSalePrice(sku) {
    if (!initialized || !sku) return null;

    const match = salePricesData.find(item => item.sku === sku);

    if (match) {
      // Parse the price string (e.g., "$29.95") to get numeric value
      const priceValue = parseFloat(match.price.replace(/[$,]/g, ''));
      return {
        sku: match.sku,
        price: match.price,
        priceValue: priceValue,
        priceCents: Math.round(priceValue * 100)
      };
    }

    return null;
  }

  /**
   * Format price in cents to money string
   * @param {number} cents - Price in cents
   * @returns {string} - Formatted price (e.g., "$29.95")
   */
  function formatMoney(cents) {
    const dollars = cents / 100;
    return `$${dollars.toFixed(2)}`;
  }

  /**
   * Update a single element with sale price
   * @param {HTMLElement} element - Element to update
   */
  function updateElement(element) {
    const sku = element.getAttribute('data-sale-price-sku');
    if (!sku) return;

    const salePrice = getSalePrice(sku);

    if (salePrice) {
      // Always store current content as original before overwriting
      // This captures the Vue-rendered price for this variant
      element.setAttribute('data-original-price', element.textContent);

      // Update the element's content with sale price
      element.textContent = salePrice.price;

      // Add a class to indicate sale price is active
      element.classList.add('has-sale-price');

      // Dispatch custom event for other scripts to listen to
      element.dispatchEvent(new CustomEvent('salePriceUpdated', {
        detail: salePrice,
        bubbles: true
      }));
    } else {
      // No sale price found - remove sale indicator
      // Don't restore original because Vue already updated the content
      if (element.classList.contains('has-sale-price')) {
        element.classList.remove('has-sale-price');
        element.removeAttribute('data-original-price');

        // Dispatch custom event
        element.dispatchEvent(new CustomEvent('salePriceRemoved', {
          bubbles: true
        }));
      }
    }
  }

  /**
   * Update all elements with data-sale-price-sku attribute
   */
  function updateAllElements() {
    const elements = document.querySelectorAll('[data-sale-price-sku]');
    elements.forEach(updateElement);
  }

  /**
   * Watch for new elements added to DOM (for dynamic content)
   */
  function watchDOM() {
    if (typeof MutationObserver !== 'undefined') {
      const observer = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          mutation.addedNodes.forEach(function(node) {
            if (node.nodeType === 1) { // Element node
              if (node.hasAttribute && node.hasAttribute('data-sale-price-sku')) {
                updateElement(node);
              }
              // Check children
              const children = node.querySelectorAll && node.querySelectorAll('[data-sale-price-sku]');
              if (children) {
                children.forEach(updateElement);
              }
            }
          });
        });
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }

  /**
   * Check if a SKU has a sale price
   * @param {string} sku - Product variant SKU
   * @returns {boolean}
   */
  function hasSalePrice(sku) {
    return getSalePrice(sku) !== null;
  }

  // Public API
  return {
    init: init,
    getSalePrice: getSalePrice,
    hasSalePrice: hasSalePrice,
    formatMoney: formatMoney,
    updateElement: updateElement,
    updateAllElements: updateAllElements,
    watchDOM: watchDOM
  };
})();
